package com.advaita.cloud.drive.shared

import kotlinx.serialization.Serializable

@Serializable
data class FileDto(
    val id: String,
    val displayName: String,
    val logicalPath: String,
    val kind: FileKind,
    val sizeBytes: Long?,
    val mimeType: String?,
    val status: Status,
    val createdAt: String,
    /**
     * The row's OWN readiness, before any pipeline overlay. [status] shows a failed or
     * running workflow on the file (FAILED / PROCESSING) even when the file itself is
     * complete; actions on the file (extract) must gate on this instead. Null from older
     * servers — fall back to [status].
     */
    val fileStatus: Status? = null,
    /** Live progress + error for a pipeline job on this file, or null. */
    val pipelineProgress: PipelineProgressDto? = null,
    /** True once a pipeline has run to completion (a SUCCEEDED job exists) on this file. */
    val pipelineProcessed: Boolean = false,
    /** Live progress + status for an in-flight modular-pull download of this file, or null. */
    val pullProgress: PullProgressDto? = null,
    /**
     * Live progress + status for an in-flight archive job whose target this row is:
     * the destination folder of an extract, or the output file of a compress. Null
     * when no archive job has ever targeted this row.
     */
    val archiveProgress: ArchiveProgressDto? = null,
    /**
     * Set while this folder is being deleted in the background, and after the delete gave up
     * (status FAILED). Null otherwise: a finished delete leaves no row to list.
     */
    val deleteProgress: DeleteProgressDto? = null,
    /** ADV-1329: the row's owner (a shared row's is not the caller). Null from older servers. */
    val ownerId: String? = null,
    /** ADV-1329: true when the row was listed through a folder the caller does not own. */
    val shared: Boolean = false,
    /**
     * ADV-1412: true when the caller created this row in another person's shared folder. It is
     * relative to the caller and names nobody else: a row another person added is false, exactly
     * like the owner's own. Set on a sharee's listings only (a folder by id, search in it, and the
     * Shared with me roots); false everywhere else, and from older servers. Example: Lena lists
     * Kai's Run 43, and her run43-a.zip is `mine`, Kai's samples.csv is not.
     */
    val mine: Boolean = false,
)

/**
 * A background folder delete. [status] is PENDING or RUNNING while it works (render progress),
 * or FAILED when it gave up (render [error]).
 */
@Serializable
data class DeleteProgressDto(
    val status: String,
    val entriesDone: Long = 0,
    /** The subtree's row count when the delete was accepted. Rows added later can pass it. */
    val entriesTotal: Long? = null,
    val error: String? = null,
)

@Serializable
enum class FileKind { FILE, FOLDER }

/** Generic post-processing readiness of a drive item (not virus-scan-specific). */
@Serializable
enum class Status { PENDING, PROCESSING, READY, NOT_APPLICABLE, FAILED }

@Serializable
data class CreateFolderRequest(
    /** The caller's own parent path. Null (with no [parentId]) means the caller's root. */
    val parentPath: String? = null,
    val name: String,
    /** ADV-1329: a parent folder by id, which may be shared. Exclusive with [parentPath]. */
    val parentId: String? = null,
)
